import { Paper, TableContainer } from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { FinancialService, IReadJustiList } from "../../../shared/services/api/financial/FinancialService";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { TableReadjust } from "./TableReadjust";
import { ToolbarReadJust } from "./ToobarReadJust";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { ModalReadJust } from "./ModalReadjust";
import dayjs from "dayjs";


export const ReadJustimentListPresentation = () => {
	const [rows, setRows] = useState<IReadJustiList[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<"create" | "edit">("create");
	const [selectedRow, setSelectedRow] = useState<any>(null);
	const toastHandler = useToast();
	const [totalCount, setTotalCount] = useState(0);
	const canAddReadjust = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.ReadjustAdd);
	const canUpdateReadjust = checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.ReadjustUpdate);

	const loadReadjustList = () => {
		setIsLoading(true);
		FinancialService.getAllReadJust()
			.then((result) => {
				setIsLoading(false);
				if (result instanceof Error) {
					const titleMessage = "Alerta!!";
					const contentMessage = "Erro: " + result?.message;
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				} else {
					setTotalCount(result.totalCount);
					setRows(result.data);
				}
			})
			.catch((err: any) => {
				setIsLoading(false);
				const { titleMessage, contentMessage } = returnMessageError(err);

				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			});
	};

	useEffect(() => {
		loadReadjustList();
	}, []);

	const handleNewReadJust = () => {
		setModalMode("create");
		setSelectedRow({ reference: null, ipca: "", incc: "", igpm: "", status: "active" });
		setIsModalOpen(true);
	};

	const handleEditReadJust = (row: any) => {
		setModalMode("edit");
		setSelectedRow(row);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedRow(null);
	};

	const normalizePercent = (value: any): number => {
		const normalized = String(value ?? "").replace(",", ".");
		const parsed = Number(normalized);
		return Number.isFinite(parsed) ? parsed : 0;
	};

	const handleSaveReadJust = async (formData: any) => {
		setIsSaving(true);
		try {
			const referenceDate: Date | null = formData?.reference ?? null;
			const reference = referenceDate ? dayjs(referenceDate).format("YYYY-MM-01") : "";
			const payload = {
				reference,
				ipca: normalizePercent(formData?.ipca),
				incc: normalizePercent(formData?.incc),
				igpm: normalizePercent(formData?.igpm),
				status: (String(formData?.status ?? "active").toLowerCase() === "inactive" ? "inactive" : "active") as "active" | "inactive",
			};

			if (!payload.reference) {
				throw new Error("Referência é obrigatória");
			}

			const result =
				modalMode === "create"
					? await FinancialService.createReadJust(payload)
					: await FinancialService.updateReadJust(String(selectedRow?.reference ?? payload.reference), payload);

			if (result instanceof Error) {
				throw result;
			}

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso!!" content={modalMode === "create" ? "Reajuste criado com sucesso!" : "Reajuste atualizado com sucesso!"} />,
			});

			handleCloseModal();
			loadReadjustList();
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<LayoutBaseDePagina
			titulo="Reajustes"
			subtitulo={"Texas de reajustes"}
			barraDeFerramentas={<ToolbarReadJust onNewReadJust={handleNewReadJust} canAdd={canAddReadjust} />}
		>
			<TableContainer
				component={Paper}
				variant="outlined"
				elevation={0}
				sx={{
					marginX: 1,
					width: "auto",
					padding: 1,
				}}
			>
				<TableReadjust
					rows={rows}
					isLoading={isLoading}
					canUpdate={canUpdateReadjust}
					onEdit={handleEditReadJust}
				/>
			</TableContainer>

			{selectedRow && (
				<ModalReadJust
					isOpen={isModalOpen}
					onClose={handleCloseModal}
					data={selectedRow}
					isLoading={isSaving}
					onSave={handleSaveReadJust}
					mode={modalMode}
				/>
			)}
		</LayoutBaseDePagina>
	);
};
